import { AnyDataPayload, UnfurlProgressData, unfurlURL } from '@lib/external/unfurl';
import { ApiError } from '@lib/network/api';
import {
  ACCEPTED_AUDIO_FILE_EXTENSIONS,
  ACCEPTED_AUDIO_FILE_EXTENSIONS_SET,
  ACCEPTED_IMAGE_FILE_EXTENSIONS,
  ACCEPTED_IMAGE_FILE_EXTENSIONS_SET,
  ACCEPTED_VIDEO_FILE_EXTENSIONS,
  ACCEPTED_VIDEO_FILE_EXTENSIONS_SET,
} from '@lib/utils/fileExtensions';
import { isHostname } from '@lib/utils/platforms';

// Legacy types removed - use AnyDataPayload from '@lib/external/unfurl' directly

/**
 * File upload validation result
 */
export interface FileValidationResult {
  accepted: boolean;
  reason?: string;
}

/**
 * URL format validation result
 */
export interface URLFormatValidationResult {
  valid: boolean;
  reason?: string;
}

/**
 * URL content validation result.
 * Now uses AnyDataPayload directly instead of the legacy UnfurlData type.
 */
export interface URLContentValidationResult {
  valid: boolean;
  reason?: string;
  unfurlData?: AnyDataPayload;
}

/**
 * File upload validation service
 */
export class AddMediaViaFileUploadValidation {
  /**
   * Checks if a file is an accepted media file (video or image)
   * Returns validation result with acceptance status and reason if rejected
   */
  isAcceptedMediaFile(file: File): FileValidationResult {
    return this.isAcceptedMedia({ mimeType: file.type, filename: file.name });
  }

  /**
   * Accepts media by raw MIME type and/or filename rather than a `File` object.
   * Same accept rules as `isAcceptedMediaFile` (video/image/audio MIME prefix,
   * or a known extension), so external sources that report a MIME type and name
   * (e.g. Google Drive folder files) validate identically to local uploads.
   */
  isAcceptedMedia({
    mimeType,
    filename,
  }: {
    mimeType?: string;
    filename?: string;
  }): FileValidationResult {
    const fileExtension = (filename || '').toLowerCase().split('.').pop() || '';
    const result =
      mimeType?.startsWith('video/') === true ||
      mimeType?.startsWith('image/') === true ||
      mimeType?.startsWith('audio/') === true ||
      ACCEPTED_VIDEO_FILE_EXTENSIONS_SET.has(fileExtension) ||
      ACCEPTED_IMAGE_FILE_EXTENSIONS_SET.has(fileExtension) ||
      ACCEPTED_AUDIO_FILE_EXTENSIONS_SET.has(fileExtension);

    if (!result) {
      console.error('Unsupported file', filename, mimeType);
      return {
        accepted: false,
        reason: 'Unsupported file type',
      };
    }

    return { accepted: true };
  }

  /**
   * Returns accepted file extensions grouped by type
   */
  getAcceptedExtensions(): {
    video: readonly string[];
    image: readonly string[];
    audio: readonly string[];
  } {
    return {
      video: ACCEPTED_VIDEO_FILE_EXTENSIONS,
      image: ACCEPTED_IMAGE_FILE_EXTENSIONS,
      audio: ACCEPTED_AUDIO_FILE_EXTENSIONS,
    };
  }
}

/**
 * YouTube URL extraction service
 */
export class AddMediaViaYouTubeURLValidation {
  /**
   * Extracts YouTube video ID from URL
   * Returns the video ID if found, null otherwise
   */
  extractYouTubeVideoId(url: string): string | null {
    try {
      const urlObj = new URL(url);

      if (this.isUrlObjYoutubeLink(urlObj)) {
        // covers /watch?v={id}
        const v = urlObj.searchParams.get('v');
        if (v && v.length >= 11) {
          return v;
        }

        // covers /embed/{id} /v/{id} /shorts/{id}
        if (
          urlObj.pathname.startsWith('/v/') ||
          urlObj.pathname.startsWith('/embed/') ||
          urlObj.pathname.startsWith('/e/') ||
          urlObj.pathname.startsWith('/shorts/') ||
          urlObj.pathname.startsWith('/live/')
        ) {
          const id = urlObj.pathname.split('/')[2];
          if (id && id.length >= 11) {
            return id;
          }
        }
      }

      if (urlObj.hostname === 'youtu.be') {
        // covers /{id}
        const id = urlObj.pathname.split('/')[1];
        if (id && id.length >= 11) {
          return id;
        }
      }
    } catch {
      // Invalid URL, return null
    }
    return null;
  }

  /**
   * Extracts YouTube playlist ID from URL
   * Returns the playlist ID if found, null otherwise
   */
  extractYouTubePlaylistId(url: string): string | null {
    try {
      const urlObj = new URL(url);
      if (!this.isUrlObjYoutubeLink(urlObj)) {
        return null;
      }

      // - youtube.com/playlist?list=... (direct playlist URL only)
      if (urlObj.pathname.startsWith('/playlist')) {
        return urlObj.searchParams.get('list') ?? null;
      }

      // - youtube.com/watch?v=..&list=...&index=1 (playlist embedded in watch url)
      if (urlObj.pathname.startsWith('/watch')) {
        return urlObj.searchParams.get('list') ?? null;
      }
    } catch {
      // Invalid URL, return null
    }
    return null;
  }

  /**
   * Extracts YouTube channel ID from URL
   * Returns the channel ID if found, null otherwise
   */
  extractYouTubeChannelId(url: string): string | null {
    try {
      const urlObj = new URL(url);
      if (!this.isUrlObjYoutubeLink(urlObj)) {
        return null;
      }

      // - youtube.com/channel/UC... (channel ID)
      if (urlObj.pathname.startsWith('/channel/')) {
        return urlObj.pathname.split('/')[2];
      }

      // - youtube.com/c/... (custom URL)
      if (urlObj.pathname.startsWith('/c/')) {
        return '@' + decodeURIComponent(urlObj.pathname.split('/')[2]);
      }

      // - youtube.com/@... (handle)
      if (urlObj.pathname.startsWith('/@')) {
        return decodeURIComponent(urlObj.pathname.split('/')[1]);
      }
    } catch {
      // Invalid URL, return null
    }
    return null;
  }

  /**
   * Helper: Checks if URL object is a YouTube link
   */
  private isUrlObjYoutubeLink(urlObj: URL): boolean {
    // Match youtube.com and subdomains (www.youtube.com, m.youtube.com, etc.)
    return isHostname(urlObj.hostname, 'youtube.com');
  }
}

/**
 * URL validation service
 */
export class AddMediaViaURLValidation {
  private youtubeValidation: AddMediaViaYouTubeURLValidation;

  constructor(youtubeValidation: AddMediaViaYouTubeURLValidation) {
    this.youtubeValidation = youtubeValidation;
  }

  /**
   * Validates URL format (basic regex check)
   * Returns validation result with acceptance status and reason if invalid
   */
  validateURLFormat(url: string): URLFormatValidationResult {
    const isValid = /^https:\/\/[^\s/$.?#].[^\s]*$/i.test(url);

    if (!isValid) {
      return {
        valid: false,
        reason: 'Please enter a valid URL.',
      };
    }

    return { valid: true };
  }

  /**
   * Validates URL content by fetching info from the server.
   * Returns validation result with acceptance status, reason if invalid, and unfurlData if valid.
   * Now returns AnyDataPayload directly instead of the legacy UnfurlData type.
   *
   * @param url - The URL to validate
   * @param signal - Optional AbortSignal to cancel the request
   * @param onProgress - Optional callback to receive progress updates during SSE streaming
   */
  async validateURLContent(
    url: string,
    signal?: AbortSignal,
    onProgress?: (progress: UnfurlProgressData) => void,
  ): Promise<URLContentValidationResult> {
    if (!url) {
      return {
        valid: false,
        reason: 'Please enter a valid URL to upload from.',
      };
    }

    try {
      // Use unfurl endpoint directly - returns AnyDataPayload
      const unfurlData = await unfurlURL(url, signal, onProgress);

      // Return the unfurl data directly - no conversion needed
      return {
        valid: true,
        unfurlData,
      };
    } catch (error) {
      // Re-throw abort errors so callers can handle them
      if (error instanceof Error && error.name === 'AbortError') {
        throw error;
      }
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        return {
          valid: false,
          reason: 'That URL seems to be invalid, private, or unsupported.',
        };
      }
      return {
        valid: false,
        reason: 'Error fetching URL info. Please try again.',
      };
    }
  }
}

/**
 * Main validation service for adding media
 * Provides access to file upload, URL, and YouTube validation services
 */
export class AddMediaValidationService {
  public readonly fileUpload: AddMediaViaFileUploadValidation;
  public readonly url: AddMediaViaURLValidation;
  public readonly youtube: AddMediaViaYouTubeURLValidation;

  constructor() {
    this.youtube = new AddMediaViaYouTubeURLValidation();
    this.url = new AddMediaViaURLValidation(this.youtube);
    this.fileUpload = new AddMediaViaFileUploadValidation();
  }
}
